import * as Sentry from '@sentry/react';
import { useIsMutating } from '@tanstack/react-query';
import React from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { requiresRequestFlow } from 'tdr-common';
import { MUTATION_KEY, useCreateCheckoutSession } from '../../api/createCheckoutSession';
import { analytics, PROCEED_TO_CHECKOUT_CLICKED } from '../../helpers/analytics';
import { useGTMAnalytics } from '../../helpers/gtm/surface/useGTMAnalytics';
import { InternalErrorToast } from '../../pages/errors/InternalErrorToast';
import { RestaurantGuardOutletContext } from '../../routes/RestaurantGuard';
import { TableGuardOutletContext } from '../../routes/TableGuard';
import CTAButton from './CTAButton';
import { useTranslation } from 'react-i18next';

type BookNowButtonProps = {
	disabled?: boolean;
	tableHasAnnouncement?: boolean;
	announcementType?: string;
	ctaType?: 'control' | 'simplified';
	priceShown?: number;
	priceCurrency?: string;
	loading?: boolean;
	textNode?: React.ReactNode;
}

export const BookNowButton = ({
	disabled = false,
	tableHasAnnouncement = false,
	announcementType = '',
	ctaType = 'control',
	priceShown,
	priceCurrency,
	loading = false,
	textNode
}: BookNowButtonProps) => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();

	const { restaurant } = useOutletContext<RestaurantGuardOutletContext>();
	const { searchFilters, table: selectedTable } = useOutletContext<TableGuardOutletContext>();
	const gtm = useGTMAnalytics();

	const { mutateAsync: createCheckoutSession } = useCreateCheckoutSession();

	const isMutating = useIsMutating({
		mutationKey: [MUTATION_KEY],
		exact: true
	});

	const filtersComplete = selectedTable && searchFilters?.groupSize && searchFilters.date && !!searchFilters.time;

	const handleCTAClick = async () => {
		try {
			if (filtersComplete) {
				// Track the event before creating checkout session
				analytics.track(PROCEED_TO_CHECKOUT_CLICKED, {
					restaurantId: restaurant.id,
					restaurantName: restaurant.name,
					tableId: selectedTable.id,
					tableName: selectedTable.name,
					isLargeGroup: requiresRequestFlow(selectedTable),
					isPatio: selectedTable.isPatio || false,
					groupSize: searchFilters.groupSize,
					date: searchFilters.date,
					time: searchFilters.time,
					tableHasAnnouncement,
					announcementType,
					ctaType,
					priceShown,
					priceCurrency
				});

				gtm.selectItem({
					itemId: selectedTable.id,
					itemName: selectedTable.name,
					itemCategory: 'Reservation',
					...(priceShown !== undefined && priceShown !== null && { price: priceShown }),
					guests: searchFilters.groupSize,
					date: searchFilters.date,
					time: searchFilters.time
				});

				const response = await createCheckoutSession({
					searchParams: searchFilters,
					tableId: selectedTable.id,
					locale: i18n.language,
					experiments: []
				});

				if (response?.success && response.checkoutSession.id) {
					navigate(`/${restaurant.slug}/checkout/${response.checkoutSession.id}`);
				}
				else {
					Sentry.captureMessage(response.message || 'Failed attempt to create checkout session');
					toast(<InternalErrorToast />, { toastId: 'internal-error', type: 'error' });
				}
			}
			else {
				const message = t('errors.missing_filters');
				Sentry.captureMessage(message);
				toast(message, { toastId: 'missing-filters', type: 'error' });
			}
		}
		catch (e) {
			if (e?.message) {
				Sentry.captureMessage(e.message);
			}
		}
	};

	return (
		<CTAButton
			loading={loading ? loading : !!isMutating}
			onClick={handleCTAClick}
			disabled={!filtersComplete || disabled}
			textNode={textNode}
			buttonText={t('actions.book_now')}
		/>
	);
};
